import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelectionCapability } from '@embedpdf/plugin-selection/react'
import { useAnnotation } from '@embedpdf/plugin-annotation/react'
import { useRedaction } from '@embedpdf/plugin-redaction/react'
import { PdfAnnotationSubtype, PdfActionType, PdfAnnotationBorderStyle } from '@embedpdf/models'
import type { Rect, PdfAnnotationFlagName } from '@embedpdf/models'
import { FloatingMenu, FloatingMenuDivider, type FloatingMenuItem } from './FloatingMenu'
import { Icon } from '../ui/icon'
import { DictionaryService, segmentKhmerWord } from '../../services/dictionary-service'
import { normalize, normalizeKhmerText } from '../../services/khmer-normalizer'

const FLAGS_DEFAULT: PdfAnnotationFlagName[] = ['print']
const MENU_HEIGHT = 44
const MENU_GAP = 4

function calculateBoundingRect(rects: Rect[]): Rect {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const r of rects) {
    if (r.origin.x < minX) minX = r.origin.x
    if (r.origin.y < minY) minY = r.origin.y
    const right = r.origin.x + r.size.width
    const bottom = r.origin.y + r.size.height
    if (right > maxX) maxX = right
    if (bottom > maxY) maxY = bottom
  }
  return {
    origin: { x: minX, y: minY },
    size: { width: maxX - minX, height: maxY - minY },
  }
}

function generateId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

interface MenuWrapperProps {
  style: React.CSSProperties
  ref: (el: HTMLDivElement | null) => void
}

interface TextSelectionMenuProps {
  documentId: string
  selected: boolean
  menuWrapperProps: MenuWrapperProps
  placement: { suggestTop: boolean }
  rect: Rect
}

export const TextSelectionMenu = ({
  documentId,
  selected,
  menuWrapperProps,
  placement,
  rect,
}: TextSelectionMenuProps) => {
  const { t } = useTranslation()
  const { provides: selectionCapability } = useSelectionCapability()
  const { provides: annotationApi } = useAnnotation(documentId)
  const { provides: redactionApi } = useRedaction(documentId)

  const [copied, setCopied] = useState(false)
  const [showLinkInput, setShowLinkInput] = useState(false)
  const [linkUrl, setLinkUrl] = useState('https://')

  useEffect(() => {
    console.log('[TextSelectionMenu] selected:', selected)
  }, [selected])

  useEffect(() => {
    if (!selected) {
      setShowLinkInput(false)
      setLinkUrl('https://')
    }
  }, [selected])

  useEffect(() => {
    if (!showLinkInput) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowLinkInput(false)
        setLinkUrl('https://')
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [showLinkInput])

  const getScope = useCallback(() => {
    if (!selectionCapability || !documentId) return null
    return selectionCapability.forDocument(documentId)
  }, [selectionCapability, documentId])

  const clearSelection = useCallback(() => {
    getScope()?.clear()
  }, [getScope])

  const handleCopy = useCallback(() => {
    const scope = getScope()
    if (!scope) return
    scope.copyToClipboard()
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [getScope])

  const handleTextMarkup = useCallback((subtype: PdfAnnotationSubtype, strokeColor: string, opacity: number) => {
    const scope = getScope()
    if (!scope || !annotationApi) {
      console.warn('[TextSelectionMenu] Cannot create markup: scope or annotationApi unavailable')
      return
    }

    const rectsByPage = scope.getHighlightRects()
    if (!rectsByPage || Object.keys(rectsByPage).length === 0) {
      console.warn('[TextSelectionMenu] No highlight rects found for selection')
      return
    }

    console.log('[TextSelectionMenu] Creating text markup:', PdfAnnotationSubtype[subtype], 'pages:', Object.keys(rectsByPage))

    for (const [pageStr, pageRects] of Object.entries(rectsByPage)) {
      const pageIndex = Number(pageStr)
      if (!pageRects || pageRects.length === 0) continue

      const boundingRect = calculateBoundingRect(pageRects)

      annotationApi.createAnnotation(pageIndex, {
        id: generateId(),
        type: subtype,
        pageIndex,
        rect: boundingRect,
        segmentRects: pageRects,
        strokeColor,
        opacity,
        contents: '',
        flags: FLAGS_DEFAULT,
      } as any)
    }

    clearSelection()
  }, [getScope, annotationApi, clearSelection])

  const handleRedact = useCallback(() => {
    const scope = getScope()
    if (!scope || !redactionApi) {
      console.warn('[TextSelectionMenu] Cannot redact: scope or redactionApi unavailable')
      return
    }

    console.log('[TextSelectionMenu] Queueing current selection as pending redaction')
    redactionApi.queueCurrentSelectionAsPending()
    clearSelection()
  }, [getScope, redactionApi, clearSelection])

  const handleLinkClick = useCallback(() => {
    setShowLinkInput(true)
  }, [])

  const handleDictionaryLookup = useCallback(async () => {
    const scope = getScope()
    if (!scope) return
    const textTask = scope.getSelectedText()
    if (!textTask) return
    textTask.wait(async (texts: string[]) => {
      const fullText = texts.join(' ').trim()
      if (!fullText) return
      const isKhmer = /[\u1780-\u17FF]/.test(fullText)
      const normalized = isKhmer ? normalizeKhmerText(fullText) : normalize(fullText)
      
      if (isKhmer) {
        const segmented = segmentKhmerWord(normalized.replace(/\s+/g, ''), 0)
        const word = segmented || normalized.replace(/\s+/g, ' ')
        ;(window as any).__DICT_WORD__ = word
        window.dispatchEvent(new CustomEvent('opakor-open-dictionary', { detail: { word } }))
      } else {
        const firstWord = normalized.split(/\s+/)[0] || normalized
        ;(window as any).__DICT_WORD__ = firstWord
        window.dispatchEvent(new CustomEvent('opakor-open-dictionary', { detail: { word: firstWord } }))
      }
    }, () => {})
  }, [getScope])

  const handleLinkSubmit = useCallback(() => {
    const scope = getScope()
    if (!scope || !annotationApi) return

    const rectsByPage = scope.getHighlightRects()
    if (!rectsByPage) return

    const url = linkUrl.trim()
    if (!url || url === 'https://') return

    for (const [pageStr, pageRects] of Object.entries(rectsByPage)) {
      const pageIndex = Number(pageStr)
      if (!pageRects || pageRects.length === 0) continue

      const boundingRect = calculateBoundingRect(pageRects)

      annotationApi.createAnnotation(pageIndex, {
        id: generateId(),
        type: PdfAnnotationSubtype.LINK,
        pageIndex,
        rect: boundingRect,
        target: {
          type: 'action' as const,
          action: {
            type: PdfActionType.URI,
            uri: url,
          },
        },
        strokeColor: '#3B82F6',
        strokeWidth: 1,
        strokeStyle: PdfAnnotationBorderStyle.UNDERLINE,
        flags: FLAGS_DEFAULT,
      } as any)
    }

    clearSelection()
    setShowLinkInput(false)
    setLinkUrl('https://')
  }, [getScope, annotationApi, linkUrl, clearSelection])

  if (!selected) return null

  const items: FloatingMenuItem[] = [
    {
      id: 'copy',
      icon: copied ? 'check' : 'copy',
      tooltip: copied ? (t('tooltips.copied') ?? 'Copied!') : (t('tooltips.copyText') ?? 'Copy Text'),
      onClick: handleCopy,
    },
    {
      id: 'dictionary',
      icon: 'book',
      tooltip: t('dashboard.dictionaryLookup') || 'Look up',
      onClick: handleDictionaryLookup,
      dividerBefore: true,
    },
    {
      id: 'underline',
      icon: 'underline',
      iconColor: '#22C55E',
      tooltip: t('tooltips.underline') ?? 'Underline',
      onClick: () => handleTextMarkup(PdfAnnotationSubtype.UNDERLINE, '#22C55E', 1),
      dividerBefore: true,
    },
    {
      id: 'strikethrough',
      icon: 'strikethrough',
      iconColor: '#EF4444',
      tooltip: t('tooltips.strikethrough') ?? 'Strikethrough',
      onClick: () => handleTextMarkup(PdfAnnotationSubtype.STRIKEOUT, '#EF4444', 1),
    },
    {
      id: 'squiggly',
      icon: 'squiggly',
      iconColor: '#EAB308',
      tooltip: t('tooltips.squigglyUnderline') ?? 'Squiggly Underline',
      onClick: () => handleTextMarkup(PdfAnnotationSubtype.SQUIGGLY, '#EAB308', 1),
    },
    {
      id: 'link',
      icon: 'link',
      iconColor: '#3B82F6',
      tooltip: t('tooltips.link') ?? 'Add Link',
      onClick: handleLinkClick,
      dividerBefore: true,
    },
    {
      id: 'redact',
      icon: 'redact',
      tooltip: t('tooltips.redactText') ?? 'Redact',
      onClick: handleRedact,
    },
  ]

  const { style: wrapperStyle, ref } = menuWrapperProps
  const menuTop = placement.suggestTop ? -(MENU_HEIGHT + MENU_GAP) : rect.size.height + MENU_GAP

  return (
    <div ref={ref} style={wrapperStyle}>
      <div
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          top: menuTop,
          zIndex: 1000,
        }}
      >
        <FloatingMenu items={items}>
          {showLinkInput && (
            <>
              <FloatingMenuDivider />
              <div className="flex items-center gap-1">
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleLinkSubmit()
                    if (e.key === 'Escape') {
                      setShowLinkInput(false)
                      setLinkUrl('https://')
                    }
                  }}
                  className="h-8 w-48 rounded-md bg-white/10 px-2 text-xs text-white placeholder-white/40 outline-none border border-white/20 focus:border-white/40"
                  placeholder="https://example.com"
                  autoFocus
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <button
                  onClick={handleLinkSubmit}
                  className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-500 text-white hover:bg-blue-600 text-xs font-bold cursor-pointer"
                >
                  <Icon icon="check" size={14} />
                </button>
              </div>
            </>
          )}
        </FloatingMenu>
      </div>
    </div>
  )
}
